import AuthorizationError from '@/common/utils/errors/AuthorizationError';
import isSelfService from '@/common/utils/isSelfService';
import RequestController from '@/common/utils/requestController';
import { OPENSTACK_AUTH_RECEIPT_HEADER } from '@/common/constants/auth.constants';
import { AxiosHeaderValue, AxiosRequestConfig, AxiosResponse } from 'axios';

export interface StoreAdapter {
  getScopedToken: () => string;
  unauthorize: () => Promise<void>;
  checkAccessDisabled: () => Promise<void>;
  checkReconfiguration: () => Promise<void>;
}

export interface HttpRequestOptions extends AxiosRequestConfig {
  unscopedRequest?: boolean;
  skipAuthGuard?: boolean;
  forceAuthGuard?: boolean;
  abortController?: AbortController;
}

export interface AuthGuardParams {
  skipAuthGuard?: boolean;
  forceAuthGuard?: boolean;
}

export default abstract class BaseHttpAdapter extends RequestController {
  readonly defaultHeaders: FetchHeaders = {
    Accept: 'application/json, text/plain, */*',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  };

  storeAdapter?: StoreAdapter;

  setAdapter(storeAdapter: StoreAdapter): void {
    this.storeAdapter = storeAdapter;
  }

  getAuthorizationHeaders(options: { unscopedRequest?: boolean }): XAuthorizationHeaders {
    const scopedToken = this.storeAdapter?.getScopedToken();
    return {
      'X-Session-Id': isSelfService() ? 1 : 0,
      ...(!options.unscopedRequest
        && scopedToken
        && { 'X-Auth-Token': scopedToken }
      )
    };
  }

  async unauthorize(): Promise<void> {
    return this.storeAdapter?.unauthorize();
  }

  protected getHeader(headers: Response['headers'] | AxiosResponse['headers'], name: string): AxiosHeaderValue | string | null {
    if (typeof headers.get === 'function') {
      return headers.get(name);
    }
    return headers[name] ?? null;
  }

  async authGuard<T extends Response | AxiosResponse>(
    response: T,
    data: ErrorBody | undefined,
    {
      skipAuthGuard,
      forceAuthGuard
    }: AuthGuardParams
  ): Promise<T> {
    if (response.status === 401) {
      if (isSelfService()) {
        if (skipAuthGuard) {
          return response;
        } else if (forceAuthGuard) {
          await this.unauthorize();
          throw new AuthorizationError(response, data);
        }

        const totpReceipt = this.getHeader(response.headers, OPENSTACK_AUTH_RECEIPT_HEADER);
        if (totpReceipt) {
          throw new AuthorizationError(response, data);
        }

        try {
          await this.storeAdapter?.checkAccessDisabled();
        } catch (e) {
          if (e instanceof AuthorizationError) {
            await this.unauthorize();
            throw new AuthorizationError(response, data);
          }
        }
      } else {
        await this.unauthorize();
        throw new AuthorizationError(response, data);
      }
    }

    return response;
  }

  async reconfigurationHook<T extends Response | AxiosResponse>(response: T): Promise<T> {
    const errorPathName = '/service-unavailable';
    const reconfigurationState = this.getHeader(response.headers, 'X-Reconfiguration-State');
    if (reconfigurationState) {
      if (isSelfService()) {
        if (window.location.pathname !== errorPathName) {
          window.location.pathname = errorPathName;
        }
      } else {
        await this.storeAdapter?.checkReconfiguration();
      }
    }
    return response;
  }

  abstract get<T = unknown>(
    path: UriPath,
    params?: FetchRequestParams,
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>>;

  abstract post<T = unknown>(
    path: UriPath,
    body?: FetchRequestBody,
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>>;

  abstract put<T = unknown>(
    path: UriPath,
    body?: FetchRequestBody,
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>>;

  abstract patch<T = unknown>(
    path: UriPath,
    body?: FetchRequestBody,
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>>;

  abstract delete<T = unknown>(
    path: UriPath,
    body?: FetchRequestBody,
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>>;
}
